import {
  AbortContext,
  IDocumentMediaPreviewData,
  MESSAGE_CONTENT_TYPE,
} from "@home-health-notify/domain";

import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import { Tooltip } from "@mui/material";
import {
  Constants,
  convertMediaToMessageData,
  OrganizationContext,
  useDebouncedAbortableCall,
  useScrollLock,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import ProgressLoader from "../loader/ProgressLoader";
import { ForwardedIcon } from "../message/MessagePanel.style";
import ForwardTo from "../message/messagePanelMenu/forwardTo/ForwardTo";
import {
  ForwardSelectedMediaButton,
  InputSearch,
  MediaActionBar,
  SelectedMediaCount,
  ToggleForwardModeButton,
} from "./OrganizationMedia.style";
import OrganizationMediaList from "./OrganizationMediaList";
import { SearchBox } from "../common/SearchBox";
import { IFetchOrganizationMediaParams } from "@home-health-notify/models";

const OrganizationMedia = () => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "organizationMedia" });
  const { enqueueSnackbar } = useSnackbar();
  const [isForwardToListModalOpen, setIsForwardToListModalOpen] =
    useState<boolean>(false);

  const handleModalOpen = () => {
    setIsForwardToListModalOpen(true);
  };

  const handleModalClose = () => {
    setIsForwardToListModalOpen(false);
  };

  const [searchText, setSearchText] = useState<string>("");
  const [forwardMode, setForwardMode] = useState<boolean>(false);
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [isFetchingMedia, setIsFetchingMedia] = useState<boolean>(false);
  const abortContextRef = useRef<AbortContext>(undefined);

  const onForwardMediaClick = () => {
    setSelectedMediaIds([]);
    setForwardMode(!forwardMode);
  };
  const { concurrencySafe } = useScrollLock();

  const onForwardClick = () => {
    handleModalOpen();
  };

  const onMediaCheck = (media: IDocumentMediaPreviewData) => {
    setSelectedMediaIds(prevSelectedMediaIds => {
      if (prevSelectedMediaIds?.includes(media.id)) {
        return prevSelectedMediaIds.filter(id => id !== media.id);
      } else {
        return [...(prevSelectedMediaIds || []), media.id];
      }
    });
  };

  const [organizationMediaList, setOrganizationMediaList] =
    useState<IDocumentMediaPreviewData[]>();

  const fetchMedia = async (query: string, context?: AbortContext) => {
    setIsFetchingMedia(true);

    const params: IFetchOrganizationMediaParams = {
      search: query,
      limit: 30,
      type: [MESSAGE_CONTENT_TYPE.IMAGE, MESSAGE_CONTENT_TYPE.VIDEO],
    } as IFetchOrganizationMediaParams;

    await selectedOrgUser?.fetchOrganizationMedia(params, context);
    if (!context || !context.isAborted) {
      const organizationMediaData = selectedOrgUser?.organizationMedia || [];
      setOrganizationMediaList(organizationMediaData);
      setIsFetchingMedia(false);
    }
  };

  const debouncedSearch = useDebouncedAbortableCall(
    async (abortContext: AbortContext, query: string) => {
      abortContextRef.current?.abort();
      abortContextRef.current = abortContext;
      await fetchMedia(query, abortContext);
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  const onScroll = async () => {
    await concurrencySafe(async () => {
      await fetchMedia(searchText);
    });
  };

  useEffect(() => {
    abortContextRef.current = new AbortContext();
    fetchMedia(searchText, abortContextRef.current);
    return () => {
      abortContextRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchTextChange = (text: string) => {
    setSearchText(text);
    setIsFetchingMedia(true);
    debouncedSearch(text);
  };
  const onSearchClear = () => {
    setSearchText("");
    setIsFetchingMedia(true);
    debouncedSearch("");
  };

  const messagesToForward = () => {
    return (organizationMediaList || [])
      ?.filter(media => selectedMediaIds?.includes(media.id))
      .map(media => convertMediaToMessageData(media));
  };

  const onForwardSuccess = () => {
    setSelectedMediaIds([]);
    setForwardMode(false);
    enqueueSnackbar("Media send successfully", { autoHideDuration: 3000 });
  };

  const showForwardMediaButton = forwardMode && selectedMediaIds.length > 0;

  return (
    <>
      {forwardMode && (
        <ForwardTo
          closeModal={handleModalClose}
          isModalOpen={isForwardToListModalOpen}
          selectedMessagesToForward={messagesToForward()}
          closeForwardMessagesActionPanel={onForwardSuccess}
        />
      )}

      <MediaActionBar>
        <InputSearch variant="outlined">
          <SearchBox
            placeholderLabel={tNs("searchMedia")}
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
            onSearchClear={onSearchClear}
          />
        </InputSearch>

        <Tooltip title={tNs("forwardMedia")}>
          <ToggleForwardModeButton
            onClick={onForwardMediaClick}
            data-testid="toggle-forward-mode-button"
          >
            <LibraryAddCheckIcon />
          </ToggleForwardModeButton>
        </Tooltip>
        {showForwardMediaButton && (
          <Tooltip title={tNs("forwardTo")}>
            <ForwardSelectedMediaButton
              onClick={onForwardClick}
              color="primary"
              data-testid="forward-selected-media-button"
            >
              <SelectedMediaCount>{selectedMediaIds.length}</SelectedMediaCount>
              <ForwardedIcon />
            </ForwardSelectedMediaButton>
          </Tooltip>
        )}
      </MediaActionBar>
      {organizationMediaList && organizationMediaList.length > 0 && (
        <OrganizationMediaList
          mediaList={organizationMediaList}
          forwardMode={forwardMode}
          onMediaCheck={onMediaCheck}
          onScroll={onScroll}
          isMediaLoading={isFetchingMedia}
          selectedMediaIds={selectedMediaIds}
        />
      )}
      {isFetchingMedia && <ProgressLoader />}
    </>
  );
};

export default OrganizationMedia;
